import { Injectable } from '@angular/core'
import { DatePipe } from '@angular/common'

import { TelPipe } from 'app/shared/pipes/tel.pipe'
import { GeographicService } from './geographic.service'

export enum AutoFormatType {
  Upper = 'upper',
  Lower = 'lower',
  Capitalize = 'capitalize',
  Integer = 'integer',
  Natural = 'natural',
  Phone = 'phone',
  PostalCode = 'postal_code',
  Currency = 'currency',
  Date = 'date',
}

@Injectable({
  providedIn: 'root'
})
export class AutoFormatService {

  constructor(
    private datePipe: DatePipe,
    private geographicService: GeographicService,
    private telPipe: TelPipe) { }

  public getFormattedValue(type: AutoFormatType, value: string, formatOptions?: any): string {
    switch (type) {
      case AutoFormatType.Upper: return value.toUpperCase()
      case AutoFormatType.Lower: return value.toLowerCase()
      case AutoFormatType.Capitalize: return this.capitalizeEachWord(value)
      case AutoFormatType.Integer: return this.formatInt(value)
      case AutoFormatType.Natural: return value.replace(/[^0-9]/g, '')
      case AutoFormatType.Phone: return this.formatPhoneNumber(value, formatOptions)
      case AutoFormatType.PostalCode: return this.formatPostalCode(value, (formatOptions || { }).country)
      case AutoFormatType.Currency: return this.formatCurrency(value)
      case AutoFormatType.Date: return this.formatDate(value, formatOptions)
      default: return value
    }
  }

  private formatPhoneNumber(value: string, formatOptions?: any): string {
    if (formatOptions && formatOptions.hasOwnProperty('usePhonePipe') && formatOptions.usePhonePipe) {
      return this.telPipe.transform(value)
    }
    return value.replace(/[^0-9]/g, '') // @todo try to use TelFilter as mask here?
  }

  private formatPostalCode(code: string, country: string): string {

    if (this.geographicService.isAmerican(country)) {
      return this.formatUSPostalCode(code)
    }

    if (this.geographicService.isCanadian(country)) {
      return this.formatCanadianPostalCode(code)
    }

    return code

  }

  private formatCanadianPostalCode(code: string): string {

    if (!code) {
      return
    }

    code = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

    if (code.length <= 3) {
      return code
    }

    return code.substring(0, 3) + ' ' + code.substring(3, 6)

  }

  private formatUSPostalCode(code: string): string {

    if (!code) {
      return
    }

    code = code.replace(/[^0-9]/g, '')

    if (code.length === 9) {
      code = code.substring(0, 5) + '-' + code.substring(5, 9)
    }

    return code

  }

  private formatInt(value: string): string {
    const isNegativeNumber = value.indexOf('-') === 0
    value = value.replace(/[^0-9]/g, '')
    return isNegativeNumber ? `-${value}` : value
  }

  private formatCurrency(value: string): string {
    const str = value.replace(/[^0-9.]/g, '')
    const number = Math.floor(parseFloat(str) * 100) / 100
    return (!str || isNaN(number)) ? '' : number.toFixed(2)
  }

  private capitalizeEachWord(value: string): string {
    return value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
  }

  private formatDate(value: string, formatOptions?: any): string {
    const dateFormat = (formatOptions) ? formatOptions : 'MMM d, y'
    return this.datePipe.transform(value, dateFormat)
  }

}
